import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/lib/AuthContext";
import { Briefcase, AlertCircle, CheckCircle2, PackageSearch, Truck, Hourglass, Wallet, CalendarDays, FileBarChart, Package } from "lucide-react";

const KPI = ({ icon: Icon, label, value, tone = "slate", to, testId }) => (
  <Link to={to || "#"} className={`block`} data-testid={testId}>
    <Card className="hover:shadow-md transition">
      <CardContent className="p-3 flex items-center gap-2.5">
        <div className={`p-2 rounded-md bg-${tone}-100 text-${tone}-700`}><Icon size={18} /></div>
        <div className="min-w-0">
          <div className="text-xs text-slate-600 truncate">{label}</div>
          <div className="font-heading text-2xl font-bold text-slate-900 leading-none">{value ?? "—"}</div>
        </div>
      </CardContent>
    </Card>
  </Link>
);

export default function TechDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/technician/dashboard").then((r) => setData(r.data)).catch((e) => setErr(e.response?.data?.detail || e.message));
  }, []);

  if (err) return (
    <div className="max-w-2xl mx-auto" data-testid="tech-dashboard-error">
      <Card><CardContent className="p-6">
        <div className="text-rose-700 font-semibold">Could not load dashboard</div>
        <div className="text-xs text-slate-600 mt-1">{String(err)}</div>
      </CardContent></Card>
    </div>
  );

  const t = data?.totals || {};
  return (
    <div className="space-y-4 max-w-2xl mx-auto" data-testid="tech-dashboard">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">My Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">{user?.name} · {data?.complaint_count ?? "—"} total complaints</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <KPI icon={Briefcase}    label="Open jobs"        value={t.open_assigned}  tone="sky"     to="/technician/reports?kind=pending" testId="kpi-open" />
        <KPI icon={AlertCircle}  label="Not visited"      value={t.not_visited}    tone="rose"    to="/my-jobs" testId="kpi-not-visited" />
        <KPI icon={CheckCircle2} label="Visited"          value={t.visited}        tone="emerald" to="/my-jobs" testId="kpi-visited" />
        <KPI icon={PackageSearch} label="Parts pending"   value={t.parts_pending}  tone="amber"   to="/technician/reports?kind=parts_pending" testId="kpi-parts-pending" />
        <KPI icon={Truck}        label="Parts delivered"  value={t.parts_delivered} tone="indigo" to="/my-jobs" testId="kpi-parts-delivered" />
        <KPI icon={CheckCircle2} label="Request to close" value={t.request_to_close} tone="teal"  to="/my-jobs" testId="kpi-request-close" />
        <KPI icon={CheckCircle2} label="Closed"           value={t.closed}         tone="slate"   to="/technician/reports?kind=completed" testId="kpi-closed" />
        <KPI icon={Wallet}       label="Paid"             value={t.paid}           tone="emerald" to="/technician/reports?kind=paid" testId="kpi-paid" />
        <KPI icon={Wallet}       label="Unpaid"           value={t.unpaid}         tone="rose"    to="/technician/reports?kind=unpaid" testId="kpi-unpaid" />
        <KPI icon={CalendarDays} label="Today's visits"   value={t.todays_visits}  tone="sky"     to="/my-jobs" testId="kpi-today-visits" />
        <KPI icon={Hourglass}    label="Overdue"          value={t.overdue}        tone="rose"    to="/my-jobs" testId="kpi-overdue" />
      </div>
      <div className="grid grid-cols-2 gap-2 pt-2">
        <Link to="/my-jobs" className="block">
          <Card><CardContent className="p-3 flex items-center gap-2"><Briefcase size={16} className="text-slate-700" /><span className="font-semibold text-slate-900">My Jobs</span></CardContent></Card>
        </Link>
        <Link to="/technician/reports" className="block" data-testid="link-reports">
          <Card><CardContent className="p-3 flex items-center gap-2"><FileBarChart size={16} className="text-slate-700" /><span className="font-semibold text-slate-900">My Reports</span></CardContent></Card>
        </Link>
        <Link to="/technician/parts-ledger" className="block col-span-2" data-testid="link-parts-ledger">
          <Card><CardContent className="p-3 flex items-center gap-2"><Package size={16} className="text-slate-700" /><span className="font-semibold text-slate-900">Parts Ledger</span></CardContent></Card>
        </Link>
      </div>
    </div>
  );
}
